// Negamax α-β with quiescence search, MVV-LVA + killer ordering and
// iterative deepening. Works on raw Positions (no GameState bookkeeping) and
// is synchronous — the Web Worker wrapper runs it off the main thread.
import { applyMove } from '../game/applyMove.js';
import { isInCheck } from '../game/attacks.js';
import { generateCastling } from '../game/castling.js';
import { generateLegalMoves } from '../game/legalMoves.js';
import { generatePseudoLegal } from '../game/moveGen.js';
import { EMPTY, type Move, type Position } from '../game/types.js';
import { evaluate } from './BoardEvaluator.js';
import { orderMoves, sameMove } from './MoveOrdering.js';

export const MATE_SCORE = 100_000;
const INFINITY = 1_000_000;
/** Scores this close to MATE_SCORE are mates; deeper mates are worse. */
const MATE_THRESHOLD = MATE_SCORE - 1_000;
const TIME_CHECK_INTERVAL = 1024;
const MAX_PLY = 64;
const KILLERS_PER_PLY = 2;

export interface SearchLimits {
  readonly maxDepth: number;
  /** Wall-clock budget; the deepest completed iteration wins. */
  readonly timeMs: number;
  /** Quiescence depth cap (captures-only plies beyond the horizon). */
  readonly quiescenceDepth: number;
}

export interface SearchResult {
  readonly best: Move | null;
  /** Centipawns from the side to move's view. */
  readonly score: number;
  readonly depth: number;
  readonly nodes: number;
}

export function isMateScore(score: number): boolean {
  return Math.abs(score) >= MATE_THRESHOLD;
}

class StopSearch extends Error {}

export class AlphaBetaSearch {
  private nodes = 0;
  private deadline = 0;
  private readonly killers: Move[][] = Array.from({ length: MAX_PLY }, () => []);

  constructor(
    private readonly limits: SearchLimits,
    private readonly now: () => number = Date.now,
  ) {}

  run(pos: Position): SearchResult {
    this.deadline = this.now() + this.limits.timeMs;
    const rootMoves = generateLegalMoves(pos);
    let best: SearchResult = { best: orderMoves(rootMoves)[0] ?? null, score: 0, depth: 0, nodes: 0 };
    if (rootMoves.length <= 1) return best;

    for (let depth = 1; depth <= this.limits.maxDepth; depth++) {
      try {
        const result = this.searchRoot(pos, rootMoves, depth, best.best);
        best = { ...result, nodes: this.nodes };
        if (isMateScore(result.score)) break;
      } catch (err) {
        if (err instanceof StopSearch) break;
        throw err;
      }
    }
    return { ...best, nodes: this.nodes };
  }

  /**
   * Full-window score of every root move at a fixed depth — used by the
   * weaker levels. Honours the time budget: when it runs out, the moves
   * scored so far are returned (at least one, so there is always a reply).
   */
  scoreRootMoves(pos: Position, depth: number): { move: Move; score: number }[] {
    this.deadline = this.now() + this.limits.timeMs;
    const scored: { move: Move; score: number }[] = [];
    for (const move of orderMoves(generateLegalMoves(pos))) {
      try {
        scored.push({ move, score: -this.negamax(applyMove(pos, move), depth - 1, -INFINITY, INFINITY, 1) });
      } catch (err) {
        if (!(err instanceof StopSearch)) throw err;
        if (scored.length === 0) scored.push({ move, score: 0 });
        break;
      }
    }
    return scored;
  }

  private searchRoot(pos: Position, moves: readonly Move[], depth: number, pv: Move | null): SearchResult {
    let alpha = -INFINITY;
    let bestMove: Move | null = null;
    for (const move of orderMoves(moves, pv, this.killers[0])) {
      const score = -this.negamax(applyMove(pos, move), depth - 1, -INFINITY, -alpha, 1);
      if (score > alpha || bestMove === null) {
        alpha = score;
        bestMove = move;
      }
    }
    return { best: bestMove, score: alpha, depth, nodes: this.nodes };
  }

  private tick(): void {
    this.nodes++;
    if (this.nodes % TIME_CHECK_INTERVAL === 0 && this.now() >= this.deadline) throw new StopSearch();
  }

  /**
   * Interior nodes check legality lazily: pseudo-legal moves are ordered
   * first, then each is applied once and skipped if it leaves the mover's
   * king attacked. Generating fully legal lists here would apply every move
   * twice.
   */
  private negamax(pos: Position, depth: number, alpha: number, beta: number, ply: number): number {
    this.tick();
    const inCheck = isInCheck(pos.board, pos.turn);
    if (ply >= MAX_PLY - 1) return evaluate(pos);
    // Check extension: never stand pat while in check — the side may be mated.
    if (depth <= 0 && !inCheck) return this.quiesce(pos, alpha, beta, ply, this.limits.quiescenceDepth);
    const nextDepth = Math.max(depth, 1) - 1;

    let a = alpha;
    let legal = 0;
    const candidates = [...generatePseudoLegal(pos), ...(inCheck ? [] : generateCastling(pos))];
    for (const move of orderMoves(candidates, null, this.killers[ply] ?? [])) {
      const child = applyMove(pos, move);
      if (isInCheck(child.board, pos.turn)) continue;
      legal++;
      const score = -this.negamax(child, nextDepth, -beta, -a, ply + 1);
      if (score >= beta) {
        if (move.captured === EMPTY) this.rememberKiller(ply, move);
        return beta;
      }
      if (score > a) a = score;
    }
    if (legal === 0) return inCheck ? -(MATE_SCORE - ply) : 0;
    return a;
  }

  private quiesce(pos: Position, alpha: number, beta: number, ply: number, depthLeft: number): number {
    this.tick();
    const standPat = evaluate(pos);
    if (standPat >= beta) return beta;
    let a = Math.max(alpha, standPat);
    if (depthLeft <= 0) return a;
    const noisy = generatePseudoLegal(pos).filter((m) => m.captured !== EMPTY || m.promotion !== EMPTY);
    for (const move of orderMoves(noisy)) {
      const child = applyMove(pos, move);
      if (isInCheck(child.board, pos.turn)) continue;
      const score = -this.quiesce(child, -beta, -a, ply + 1, depthLeft - 1);
      if (score >= beta) return beta;
      if (score > a) a = score;
    }
    return a;
  }

  private rememberKiller(ply: number, move: Move): void {
    const slot = this.killers[ply];
    if (slot === undefined) return;
    if (slot[0] !== undefined && sameMove(slot[0], move)) return;
    slot.unshift(move);
    if (slot.length > KILLERS_PER_PLY) slot.length = KILLERS_PER_PLY;
  }
}
